import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material";
import { CreateRuleForm } from "./CreateRuleForm";
import type { CreateRuleFormProps } from "./CreateRuleForm";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";

export type CreateRuleDialogViewProps = Omit<
  CreateRuleFormProps,
  "templateCardSize" | "composerSurface"
> & {
  open: boolean;
  onClose: () => void;
};

/**
 * The rule creation form as a modal, opened from the rules list.
 *
 * The dialog carries no action buttons: every way out of it is a choice inside
 * the form — build with Atlas, pick a template, or write the code yourself —
 * and a trailing "Create" would name none of them.
 */
export const CreateRuleDialogView = ({
  open,
  onClose,
  ...formProps
}: CreateRuleDialogViewProps) => {
  const theme = useTheme();

  return (
    <IgDialog
      showDialog={open}
      title="Create a new rule"
      subtitle={
        <Box sx={{ mt: "-16px" }}>
          Describe which products to show and to whom, or start from a template
          and Atlas fills in the rest.
        </Box>
      }
      maxWidth="md"
      minWidth="0"
      // The close sits in the title row rather than IgDialog's floating 40px
      // button, so the title takes the full row and the icon lands 24px from
      // the dialog's right edge.
      dialogTitleWidth="100%"
      headerEndAdornment={
        <IgIconButton
          aria-label="close"
          iconType="close"
          iconSize={24}
          onClick={onClose}
          sx={{
            // A bare 24px icon on the title's 24px line, at least 12px from it.
            // Block-level, so the grid cell does not add a line box under it.
            display: "flex",
            ml: 3,
            p: 0,
            // The hit area stays 40px without taking any layout.
            "&::before": { content: '""', inset: "-8px", position: "absolute" },
          }}
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          // 850px of which 802px is content. The 1px stroke is drawn inside
          // the edge as in Figma rather than as a border that would take the
          // content down to 800px.
          boxShadow: `inset 0 0 0 1px ${theme.igBorders.core.default}, ${theme.igShadows.high}`,
          maxWidth: "calc(100% - 64px)",
          width: "850px",
        },
        // MUI's DialogContent scrolls on the y axis, which per spec forces the
        // x axis to clip too — shearing the composer's focus glow off at its
        // left and right edges. The dialog's own 24px padding is the room it
        // needs.
        "& .MuiDialogContent-root": { overflow: "visible" },
      }}
      hideActions
      handleClose={onClose}
      rawContent={
        // 24px from the subtitle to the composer: DialogContent's 24px top
        // padding on IgDialog's 16px stack gap, less 16.
        <Box sx={{ mt: "-16px" }}>
          <CreateRuleForm
            {...formProps}
            compactPlaceholder
            templateCardSize={{ xs: 10, sm: 5 }}
            composerSurface="plain"
          />
        </Box>
      }
    />
  );
};
